import React, { Fragment, useMemo } from "react";
import ColumnText from "../molecules/ColumnText";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import PaginationUI from "../atoms/PaginationUI";
import { TableLoader } from "../molecules/TableLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { UI } from "@/shared/utils/ui";
import { Empty } from "./Empty";

export type DataTableAction<T> = {
  label?: string;
  icon?: IconDefinition;
  handler: (item: T, index: number) => void;
  state: UI.State;
};

export type DataTableColumn<T, K extends keyof T = keyof T> = {
  label?: string;
  field?: K;
  mapper?: (value: T[K], item: T) => any;
  type?: "text" | "color" | "number" | "date" | "boolean";
};

export type DataTableProps<T> = {
  isLoading?: boolean;
  data: Array<T>;
  currentPage?: number;
  totalPages?: number;
  onPageChange?: (page: number) => void;
  pageSize?: number;
  totalResults?: number;
  actions: Array<DataTableAction<T>>;
  columns: Array<DataTableColumn<T>>;
  placeholder?: string;
  onEmptyClick?: () => void;
};

export function DataTable<T = any>({
  isLoading,
  data,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  totalResults,
  columns,
  actions,
  placeholder,
  onEmptyClick,
}: DataTableProps<T>) {
  const items = useMemo(() => {
    return data.map((item) => {
      return columns.reduce((row, column) => {
        const value =
          typeof column.mapper === "function"
            ? column.mapper(item[column.field], item)
            : item[column.field];

        return [...row, value];
      }, []);
    });
  }, [data, columns]);

  const paginationVisible = useMemo(() => {
    return (
      totalResults !== undefined &&
      currentPage !== undefined &&
      totalPages !== undefined &&
      pageSize !== undefined &&
      data.length > 0
    );
  }, [totalResults, currentPage, totalPages, pageSize]);

  return (
    <div className="flex flex-col">
      {isLoading ? (
        <TableLoader columns={columns.length} rows={12} />
      ) : (
        <Fragment>
          {placeholder && data.length === 0 ? (
            <Empty texto={placeholder} onClick={onEmptyClick} />
          ) : (
            <table className="divide-y divide-gray-300">
              <thead>
                <tr>
                  {columns?.map((column) => (
                    <th
                      scope="col"
                      className="p-4 text-left text-sm font-semibold text-gray-900"
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {items.map((item: any, index) => (
                  <tr key={item.id}>
                    {item?.map((value: any) => {
                      return <ColumnText wrap title={value} />;
                    })}

                    {actions?.length > 0 && (
                      <td className="p-4 whitespace-nowrap flex gap-4 justify-end">
                        {actions?.map((action, actionIndex) => (
                          <button
                            key={actionIndex}
                            onClick={() => action.handler(data[index], index)}
                            className={`flex gap-2 items-center ${UI.stateTextColor[action.state ?? "secondary"]}`}
                          >
                            {action.icon && (
                              <FontAwesomeIcon icon={action.icon} />
                            )}
                            {action.label}
                          </button>
                        ))}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Fragment>
      )}
      {paginationVisible && (
        <PaginationUI
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={onPageChange}
          totalResults={totalResults}
        />
      )}
    </div>
  );
}
